import { AlertCircle, CheckCircle2, Clock, ExternalLink, Loader2, XCircle } from 'lucide-react';

const Row = ({ label, value, testId, className = 'text-white' }) => (
  <div className="flex justify-between text-sm">
    <span className="text-zinc-400">{label}</span>
    <span className={className} data-testid={testId}>{value}</span>
  </div>
);

export const PaymentPreview = ({ preview, loading, error }) => {
  if (loading) {
    return (
      <div className="bg-zinc-800/50 rounded-md p-4 flex items-center gap-2 text-sm text-zinc-400" data-testid="payment-preview-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking balances on Solana...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-md p-3 flex gap-2 text-sm text-red-400" data-testid="payment-preview-error">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
      </div>
    );
  }
  if (!preview) return null;

  const ok = preview.sufficient_usdc && preview.sufficient_sol;
  return (
    <div className="bg-zinc-800/50 rounded-md p-4 space-y-2" data-testid="payment-preview">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Payment preview</div>
      <Row label="USDC balance" value={`$${preview.usdc_balance.toFixed(2)}`} testId="preview-usdc-balance"
        className={preview.sufficient_usdc ? 'text-green-500' : 'text-red-500'} />
      <Row label="SOL balance" value={`${preview.sol_balance.toFixed(4)} SOL`} testId="preview-sol-balance"
        className={preview.sufficient_sol ? 'text-green-500' : 'text-red-500'} />
      <Row label="Total to send" value={`$${preview.total_amount.toFixed(2)} USDC`} testId="preview-total" />
      <Row label="Recipients / transactions" value={`${preview.transfer_count} / ${preview.tx_count}`} testId="preview-tx-count" />
      <Row label="New USDC accounts" value={`${preview.new_accounts.length} (${preview.new_accounts_rent_sol.toFixed(4)} SOL)`} testId="preview-new-accounts" />
      <Row label="Estimated SOL needed" value={`~${preview.estimated_sol_needed.toFixed(5)} SOL`} testId="preview-sol-needed" />
      {preview.new_accounts.length > 0 && (
        <div className="text-xs text-zinc-500" data-testid="preview-new-accounts-list">
          New accounts for: {preview.new_accounts.map(a => a.names.join(', ')).join(' · ')}
        </div>
      )}
      {preview.warning && <div className="text-xs text-amber-400">{preview.warning}</div>}
      {!ok && (
        <div className="text-sm text-red-400 border-t border-zinc-700 pt-2" data-testid="preview-insufficient">
          {!preview.sufficient_usdc ? 'Not enough USDC for this batch.' : 'Not enough SOL to cover fees and new accounts.'}
        </div>
      )}
    </div>
  );
};

const STATUS = {
  signing: { icon: Clock, cls: 'text-zinc-400', label: 'Waiting for signature' },
  sent: { icon: Loader2, cls: 'text-blue-400 animate-spin', label: 'Sent, confirming...' },
  pending: { icon: Clock, cls: 'text-amber-400', label: 'Pending' },
  confirmed: { icon: CheckCircle2, cls: 'text-green-500', label: 'Confirmed - paid' },
  failed: { icon: XCircle, cls: 'text-red-500', label: 'Failed - invoices stay open' },
  cancelled: { icon: XCircle, cls: 'text-zinc-500', label: 'Cancelled' },
};

export const TxProgressList = ({ items }) => {
  if (!items.length) return null;
  return (
    <div className="bg-zinc-800/30 rounded-md border border-zinc-800" data-testid="tx-progress-list">
      {items.map(t => {
        const st = STATUS[t.status] || STATUS.pending;
        const Icon = st.icon;
        return (
          <div key={t.index} className="p-3 border-b border-zinc-800 last:border-b-0 text-sm" data-testid={`tx-progress-${t.index}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Icon className={`w-4 h-4 shrink-0 ${st.cls}`} />
                <span className="text-white">Tx {t.index + 1}</span>
                <span className="text-zinc-500 truncate">{t.transfer_count} transfers · ${t.amount.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-zinc-400" data-testid={`tx-status-${t.index}`}>{st.label}</span>
                {t.signature && (
                  <a href={`https://solscan.io/tx/${t.signature}`} target="_blank" rel="noreferrer"
                    className="text-blue-500 hover:text-blue-400" data-testid={`tx-solscan-${t.index}`}>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
            <div className="text-xs text-zinc-500 mt-1 truncate">{t.names.join(', ')}</div>
            {t.error && <div className="text-xs text-red-400 mt-1 break-all">{t.error}</div>}
          </div>
        );
      })}
    </div>
  );
};
